import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  ArrowLeft, Database, Plus, Search, Filter, Edit, Trash2,
  CheckCircle, Clock, XCircle, Syringe, RefreshCw, AlertTriangle,
  ChevronDown, ChevronUp, BarChart3, Download, Upload, Eye
} from "lucide-react";

// Species options
const SPECIES_OPTIONS = [
  { value: "canine", label: "Cane" },
  { value: "feline", label: "Gatto" },
  { value: "equine", label: "Cavallo" },
  { value: "rabbit", label: "Coniglio" },
  { value: "bovine", label: "Bovino" },
  { value: "ovine", label: "Ovino" },
];

// Protection types
const PROTECTION_TYPES = [
  "rabies", "distemper", "parvovirus", "hepatitis", "leptospirosis",
  "parainfluenza", "bordetella", "leishmaniasis", "panleukopenia",
  "calicivirus", "herpesvirus", "chlamydia", "feline_leukemia",
  "myxomatosis", "rhd", "tetanus", "influenza", "strangles"
];

export default function AdminKB() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vaccines, setVaccines] = useState([]);
  const [stats, setStats] = useState(null);
  const [matchLogs, setMatchLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddVaccine, setShowAddVaccine] = useState(false);
  const [showEditVaccine, setShowEditVaccine] = useState(false);
  const [editingVaccine, setEditingVaccine] = useState(null);
  const [showUnmatched, setShowUnmatched] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    species: "",
    manufacturer: "",
    status: "",
    search: ""
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error("Accesso non autorizzato");
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, page, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams({ page: page.toString(), limit: "20" });
      if (filters.species) params.append("species", filters.species);
      if (filters.manufacturer) params.append("manufacturer", filters.manufacturer);
      if (filters.status) params.append("status", filters.status);
      
      const [vaccinesRes, statsRes] = await Promise.all([
        authFetch(`${API}/kb/vaccines?${params}`),
        authFetch(`${API}/kb/stats`)
      ]);
      
      if (vaccinesRes.ok) {
        const data = await vaccinesRes.json();
        let filteredVaccines = data.vaccines || [];
        
        // Client-side search filter
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filteredVaccines = filteredVaccines.filter(v => 
            v.commercial_name?.toLowerCase().includes(searchLower) ||
            v.manufacturer?.toLowerCase().includes(searchLower) ||
            v.aliases?.some(a => a.toLowerCase().includes(searchLower))
          );
        }
        
        setVaccines(filteredVaccines);
        setTotalPages(data.pages || 1);
      }
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error("Error fetching KB data:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const fetchUnmatchedLogs = async () => {
    try {
      const res = await authFetch(`${API}/kb/analytics/unmatched`);
      if (res.ok) {
        const data = await res.json();
        setMatchLogs(data.unmatched || []);
        setShowUnmatched(true);
      }
    } catch (error) {
      console.error("Error fetching unmatched:", error);
    }
  };

  const handleDeleteVaccine = async (kbId) => {
    if (!confirm("Eliminare questo vaccino dalla Knowledge Base?")) return;
    
    try {
      const res = await authFetch(`${API}/kb/vaccines/${kbId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Vaccino eliminato");
        fetchData();
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleValidate = async (kbId, status) => {
    try {
      const res = await authFetch(`${API}/kb/vaccines/${kbId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ validation_status: status })
      });
      if (res.ok) {
        toast.success(status === "validated" ? "Vaccino validato!" : "Stato aggiornato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "validated":
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs"><CheckCircle size={12} /> Validato</span>;
      case "rejected":
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs"><XCircle size={12} /> Rifiutato</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs"><Clock size={12} /> In attesa</span>;
    }
  };

  if (loading && vaccines.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Database className="animate-pulse text-blue-500" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/dashboard')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Database className="text-blue-500" size={24} />
                  Knowledge Base Vaccini
                </h1>
                <p className="text-sm text-gray-500">Gestione database vaccini autorizzati</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchUnmatchedLogs} className="rounded-xl">
                <AlertTriangle size={16} className="mr-2" />
                Non riconosciuti
              </Button>
              <Button onClick={() => setShowAddVaccine(true)} className="bg-blue-600 hover:bg-blue-700 rounded-xl">
                <Plus size={16} className="mr-2" />
                Aggiungi Vaccino
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <Syringe className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_vaccines}</p>
                  <p className="text-xs text-gray-500">Totale vaccini</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <CheckCircle className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.validated}</p>
                  <p className="text-xs text-gray-500">Validati</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                  <Clock className="text-yellow-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pending_validation}</p>
                  <p className="text-xs text-gray-500">In attesa</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                  <BarChart3 className="text-purple-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {Object.keys(stats.top_manufacturers || {}).length}
                  </p>
                  <p className="text-xs text-gray-500">Produttori</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Cerca vaccino..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 rounded-xl"
                />
              </div>
            </div>
            <Select value={filters.species || "all"} onValueChange={(v) => setFilters({ ...filters, species: v === "all" ? "" : v })}>
              <SelectTrigger className="w-[150px] rounded-xl">
                <SelectValue placeholder="Specie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {SPECIES_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
              <SelectTrigger className="w-[150px] rounded-xl">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="validated">Validati</SelectItem>
                <SelectItem value="pending">In attesa</SelectItem>
                <SelectItem value="rejected">Rifiutati</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData} className="rounded-xl">
              <RefreshCw size={16} />
            </Button>
          </div>
        </div>

        {/* Vaccines Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Produttore</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Specie</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Protezioni</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Durata</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Stato</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Voti</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {vaccines.map((vaccine) => (
                  <tr key={vaccine.kb_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{vaccine.commercial_name}</p>
                        {vaccine.aliases?.length > 0 && (
                          <p className="text-xs text-gray-500">Alias: {vaccine.aliases.join(", ")}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {vaccine.manufacturer || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {vaccine.target_species?.map(s => (
                          <span key={s} className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {vaccine.protection_types?.slice(0, 3).map(p => (
                          <span key={p} className="px-1.5 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs">
                            {p}
                          </span>
                        ))}
                        {vaccine.protection_types?.length > 3 && (
                          <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded text-xs">
                            +{vaccine.protection_types.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {vaccine.duration_months ? `${vaccine.duration_months} mesi` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(vaccine.validation_status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {vaccine.total_votes || 0}
                      {vaccine.vet_votes > 0 && (
                        <span className="text-green-600 ml-1">({vaccine.vet_votes} vet)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {vaccine.validation_status !== "validated" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleValidate(vaccine.kb_id, "validated")}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <CheckCircle size={16} />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setEditingVaccine(vaccine); setShowEditVaccine(true); }}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteVaccine(vaccine.kb_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {vaccines.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <Database className="mx-auto mb-3 text-gray-300" size={40} />
              <p>Nessun vaccino trovato</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Pagina {page} di {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Precedente
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Successiva
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Vaccine Dialog */}
      <AddVaccineDialog 
        open={showAddVaccine} 
        onClose={() => setShowAddVaccine(false)} 
        onSuccess={() => { setShowAddVaccine(false); fetchData(); }}
      />

      {/* Edit Vaccine Dialog */}
      <EditVaccineDialog 
        open={showEditVaccine} 
        vaccine={editingVaccine}
        onClose={() => { setShowEditVaccine(false); setEditingVaccine(null); }} 
        onSuccess={() => { setShowEditVaccine(false); setEditingVaccine(null); fetchData(); }}
      />

      {/* Unmatched Vaccines Dialog */}
      <UnmatchedDialog 
        open={showUnmatched}
        logs={matchLogs}
        onClose={() => setShowUnmatched(false)}
        onAddToKB={(name) => {
          setShowUnmatched(false);
          setShowAddVaccine(true);
        }}
      />
    </div>
  );
}

// Add Vaccine Dialog
function AddVaccineDialog({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    commercial_name: "",
    generic_name: "",
    manufacturer: "",
    target_species: [],
    protection_types: [],
    duration_months: "",
    booster_schedule: "",
    ema_code: "",
    aic_code: "",
    aliases: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        ...form,
        target_species: form.target_species,
        protection_types: form.protection_types,
        duration_months: form.duration_months ? parseInt(form.duration_months) : null,
        aliases: form.aliases ? form.aliases.split(",").map(a => a.trim()) : []
      };
      
      const res = await authFetch(`${API}/kb/vaccines`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success("Vaccino aggiunto alla KB!");
        onSuccess();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const toggleSpecies = (species) => {
    setForm(f => ({
      ...f,
      target_species: f.target_species.includes(species)
        ? f.target_species.filter(s => s !== species)
        : [...f.target_species, species]
    }));
  };

  const toggleProtection = (protection) => {
    setForm(f => ({
      ...f,
      protection_types: f.protection_types.includes(protection)
        ? f.protection_types.filter(p => p !== protection)
        : [...f.protection_types, protection]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="text-blue-500" size={20} />
            Aggiungi Vaccino alla KB
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nome commerciale *</Label>
              <Input
                value={form.commercial_name}
                onChange={(e) => setForm({ ...form, commercial_name: e.target.value })}
                placeholder="es. Nobivac DHPPi"
                required
              />
            </div>
            <div>
              <Label>Nome generico</Label>
              <Input
                value={form.generic_name}
                onChange={(e) => setForm({ ...form, generic_name: e.target.value })}
                placeholder="es. Vaccino polivalente"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Produttore</Label>
              <Input
                value={form.manufacturer}
                onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                placeholder="es. MSD Animal Health"
              />
            </div>
            <div>
              <Label>Durata (mesi)</Label>
              <Input
                type="number"
                value={form.duration_months}
                onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
                placeholder="es. 12"
              />
            </div>
          </div>

          <div>
            <Label>Specie target</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {SPECIES_OPTIONS.map(s => (
                <label key={s.value} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.target_species.includes(s.value)}
                    onCheckedChange={() => toggleSpecies(s.value)}
                  />
                  <span className="text-sm">{s.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Protezioni</Label>
            <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
              {PROTECTION_TYPES.map(p => (
                <label key={p} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.protection_types.includes(p)}
                    onCheckedChange={() => toggleProtection(p)}
                  />
                  <span className="text-sm capitalize">{p.replace("_", " ")}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Codice EMA</Label>
              <Input
                value={form.ema_code}
                onChange={(e) => setForm({ ...form, ema_code: e.target.value })}
                placeholder="EMEA/V/C/..."
              />
            </div>
            <div>
              <Label>Codice AIC</Label>
              <Input
                value={form.aic_code}
                onChange={(e) => setForm({ ...form, aic_code: e.target.value })}
                placeholder="103428..."
              />
            </div>
          </div>

          <div>
            <Label>Alias (separati da virgola)</Label>
            <Input
              value={form.aliases}
              onChange={(e) => setForm({ ...form, aliases: e.target.value })}
              placeholder="es. Nobivac DHPP, Nobivac Puppy"
            />
          </div>

          <div>
            <Label>Schema richiami</Label>
            <Input
              value={form.booster_schedule}
              onChange={(e) => setForm({ ...form, booster_schedule: e.target.value })}
              placeholder="es. Annuale, Ogni 3 anni"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? "Salvataggio..." : "Aggiungi"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Edit Vaccine Dialog
function EditVaccineDialog({ open, vaccine, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (vaccine) {
      setForm({
        ...vaccine,
        aliases: vaccine.aliases?.join(", ") || ""
      });
    }
  }, [vaccine]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!vaccine) return;
    
    setLoading(true);
    try {
      const payload = {
        ...form,
        duration_months: form.duration_months ? parseInt(form.duration_months) : null,
        aliases: form.aliases ? form.aliases.split(",").map(a => a.trim()) : []
      };
      
      const res = await authFetch(`${API}/kb/vaccines/${vaccine.kb_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success("Vaccino aggiornato!");
        onSuccess();
      } else {
        toast.error("Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  if (!vaccine) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="text-blue-500" size={20} />
            Modifica {vaccine.commercial_name}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Nome commerciale</Label>
            <Input
              value={form.commercial_name || ""}
              onChange={(e) => setForm({ ...form, commercial_name: e.target.value })}
            />
          </div>
          
          <div>
            <Label>Produttore</Label>
            <Input
              value={form.manufacturer || ""}
              onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
            />
          </div>

          <div>
            <Label>Durata (mesi)</Label>
            <Input
              type="number"
              value={form.duration_months || ""}
              onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
            />
          </div>

          <div>
            <Label>Alias (separati da virgola)</Label>
            <Input
              value={form.aliases || ""}
              onChange={(e) => setForm({ ...form, aliases: e.target.value })}
            />
          </div>

          <div>
            <Label>Stato validazione</Label>
            <Select value={form.validation_status} onValueChange={(v) => setForm({ ...form, validation_status: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">In attesa</SelectItem>
                <SelectItem value="validated">Validato</SelectItem>
                <SelectItem value="rejected">Rifiutato</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? "Salvataggio..." : "Salva"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Unmatched Vaccines Dialog
function UnmatchedDialog({ open, logs, onClose, onAddToKB }) {
  // Group by vaccine name
  const grouped = logs.reduce((acc, log) => {
    const name = log.vaccine_extracted?.nome_estratto || "Sconosciuto";
    if (!acc[name]) {
      acc[name] = { name, count: 0, samples: [] };
    }
    acc[name].count++;
    if (acc[name].samples.length < 3) {
      acc[name].samples.push(log);
    }
    return acc;
  }, {});

  const sortedGroups = Object.values(grouped).sort((a, b) => b.count - a.count);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-yellow-500" size={20} />
            Vaccini Non Riconosciuti
          </DialogTitle>
        </DialogHeader>
        
        {sortedGroups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="mx-auto mb-3 text-green-500" size={40} />
            <p>Tutti i vaccini sono stati riconosciuti!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedGroups.map(group => (
              <div key={group.name} className="p-4 border rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{group.name}</p>
                    <p className="text-sm text-gray-500">{group.count} occorrenze</p>
                  </div>
                  <Button size="sm" onClick={() => onAddToKB(group.name)} className="rounded-xl">
                    <Plus size={14} className="mr-1" /> Aggiungi a KB
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
